import { SolarRecommendedApplianceFieldsFragment } from '@/gql/graphql';
import { Link } from 'react-router-dom';
import {
  ApplianceThumb,
  specChips,
} from '@/ui/pages/estimateBuilderPage/components/detailPanel/components/applianceDisplay';
import {
  Alert,
  AnchorButton,
  Button,
  ButtonRadioGroup,
  ButtonRadioGroupOrientationVariant,
  ButtonRadioGroupProps,
  Checkbox,
  Field,
  Form,
  H4,
  IncrementableNumberInput,
  Text,
  Tooltip,
} from '@payaca/ambrosia';
import CurrencyValue from '@payaca/components/currencyValue/CurrencyValue';
import { type SolarElectricalTopology } from '@payaca/helpers/solarElectricalTopology';
import { installedBatteryCapacityKwh } from '@payaca/helpers/solarInstalledEquipment';
import UntitledIcon from '@payaca/untitled-icons';
import { FC, useId, useState, useRef, useEffect, type ReactNode } from 'react';
import { type InspectorFocusRequest } from './InspectorNavigation';
import { Controller, useFormContext, useForm } from 'react-hook-form';

/** A one-sentence fact behind an (i), read on hover or keyboard focus. */
const Hint: FC<{ text: string }> = ({ text }) => (
  <Tooltip tooltipContent={text}>
    <span
      role="img"
      aria-label={text}
      tabIndex={0}
      className="inline-flex text-gray-500"
    >
      <UntitledIcon name="info-circle.3" className="size-4" />
    </span>
  </Tooltip>
);

type RecommendedAppliance = SolarRecommendedApplianceFieldsFragment;
type ApplianceKind = 'panel' | 'inverter' | 'battery';
type EquipmentValues = Record<ApplianceKind, string> & {
  includeBattery: boolean;
  batteryQuantity: number;
};

type ApplianceSummaryProps = {
  recommendation: RecommendedAppliance;
  panelsCount?: number;
  unitQuantity?: number;
  /** Only show suggestion/selection states inside the open picker. */
  selection?: 'selected' | 'suggested';
  /** A control on the same row, such as a quantity stepper. */
  trailing?: ReactNode;
};

const ApplianceSummary: FC<ApplianceSummaryProps> = (props) => {
  const { recommendation, panelsCount, unitQuantity, selection, trailing } =
    props;
  const quantity = unitQuantity ?? panelsCount ?? recommendation.quantity;
  const { appliance } = recommendation;
  const chips = specChips(appliance.productSpec);

  return (
    <div className="flex w-full items-start gap-3 text-left">
      <ApplianceThumb imageUrl={appliance.productSpec.image?.url} size="sm" />
      <div className="min-w-0 flex-1 space-y-1">
        <Text>
          <strong>{appliance.productSpec.name}</strong>
        </Text>
        {chips && <Text level="supporting">{chips}</Text>}
        {quantity != null ? (
          <div>
            <Text>
              {quantity} × <CurrencyValue {...appliance.totalPrice} /> ={' '}
              <strong>
                <CurrencyValue
                  currency={appliance.totalPrice.currency}
                  value={appliance.totalPrice.value * quantity}
                />
              </strong>
            </Text>
          </div>
        ) : (
          <Text>
            <CurrencyValue {...appliance.totalPrice} />
          </Text>
        )}
        {selection && (
          <Text level="supporting">
            {selection === 'selected' ? 'Selected' : 'Suggested'}
          </Text>
        )}
      </div>
      {trailing && <div className="shrink-0">{trailing}</div>}
    </div>
  );
};

type ApplianceOptionMetadata = Omit<
  ApplianceSummaryProps,
  'selection' | 'trailing'
> & {
  suggested: boolean;
};

const ApplianceOption: NonNullable<
  ButtonRadioGroupProps<string, ApplianceOptionMetadata>['CustomOption']
> = (props) => {
  const { option, checked } = props;
  if (!option.metadata) return null;
  const { recommendation, panelsCount, unitQuantity, suggested } =
    option.metadata;
  let selection: ApplianceSummaryProps['selection'];
  if (checked) selection = 'selected';
  else if (suggested) selection = 'suggested';

  return (
    <ApplianceSummary
      recommendation={recommendation}
      panelsCount={panelsCount}
      unitQuantity={unitQuantity}
      selection={selection}
    />
  );
};

const ApplianceSection: FC<{
  name: ApplianceKind;
  title: string;
  recommendations: RecommendedAppliance[];
  selectedApplianceId?: string;
  panelsCount?: number;
  unitQuantity?: number;
  emptyText: string;
  expanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
  onSelect: (applianceId: string) => void;
  suggestedApplianceId?: string;
  hasReviewOptions?: boolean;
  /** Shown on the selected product's row, such as a quantity stepper. */
  trailing?: ReactNode;
}> = (props) => {
  const {
    name,
    title,
    recommendations,
    selectedApplianceId,
    panelsCount,
    unitQuantity,
    emptyText,
    expanded,
    onExpandedChange,
    onSelect,
    suggestedApplianceId,
  } = props;
  const { control } = useFormContext<EquipmentValues>();
  const sectionId = useId();
  const selected = recommendations.find(
    ({ appliance }) => appliance.id === selectedApplianceId
  );
  const hasOptions = recommendations.length > 0;
  let changeLabel = name === 'inverter' ? 'Review options' : 'Choose';
  if (selected) changeLabel = 'Change';
  let unselectedText = `Choose ${title.toLowerCase()} for this design.`;
  if (name === 'inverter')
    unselectedText = selectedApplianceId
      ? 'Selected inverter unavailable · check details'
      : 'Inverter not selected · panel-only pricing';

  return (
    <section aria-label={title} className="space-y-2">
      <div className="flex items-center justify-between gap-3">
        <H4>{title}</H4>
        {(hasOptions || props.hasReviewOptions) && (
          <Button
            variant="quaternary"
            size="sm"
            aria-label={
              expanded
                ? `Done choosing ${title.toLowerCase()}`
                : `Change ${title.toLowerCase()}`
            }
            aria-expanded={expanded}
            aria-controls={expanded ? sectionId : undefined}
            onClick={() => onExpandedChange(!expanded)}
          >
            {expanded ? 'Done' : changeLabel}
          </Button>
        )}
      </div>
      {!hasOptions && <Text level="supporting">{emptyText}</Text>}
      {hasOptions && expanded && (
        <div id={sectionId} className="space-y-3">
          <Controller
            control={control}
            name={name}
            render={({ field: { name: fieldName, value, onChange } }) => (
              <Field name={fieldName}>
                <span className="sr-only">
                  <Field.Label>Choose {title.toLowerCase()}</Field.Label>
                </span>
                <ButtonRadioGroup
                  aria-label={`Choose ${title.toLowerCase()}`}
                  value={value}
                  onChange={(id) => {
                    onChange(id);
                    onSelect(id);
                  }}
                  orientationVariant={
                    ButtonRadioGroupOrientationVariant.VERTICAL
                  }
                  options={recommendations.map((recommendation, index) => ({
                    value: recommendation.appliance.id,
                    label: recommendation.appliance.productSpec.name,
                    metadata: {
                      recommendation,
                      panelsCount,
                      unitQuantity:
                        name === 'inverter' &&
                        recommendation.appliance.productSpec.category !==
                          'BATTERY'
                          ? undefined
                          : unitQuantity,
                      suggested:
                        index === 0 &&
                        (name !== 'inverter' ||
                          recommendation.appliance.id === suggestedApplianceId),
                    } satisfies ApplianceOptionMetadata,
                  }))}
                  CustomOption={ApplianceOption}
                />
              </Field>
            )}
          />
        </div>
      )}
      {hasOptions && !expanded && selected && (
        <ApplianceSummary
          recommendation={selected}
          panelsCount={panelsCount}
          unitQuantity={unitQuantity}
          trailing={props.trailing}
        />
      )}
      {hasOptions && !expanded && !selected && (
        <Text level="supporting">{unselectedText}</Text>
      )}
    </section>
  );
};

type ProductsPanelProps = {
  focusRequest?: InspectorFocusRequest | null;
  onFocusHandled?: () => void;
  recommendedPanelAppliances: RecommendedAppliance[];
  recommendedInverterAppliances: RecommendedAppliance[];
  excludedInverterAppliances?: {
    exclusionReason?: string | null;
    blockingReason?: string | null;
    appliance: { id: string; productSpec: { name: string } };
  }[];
  recommendedBatteryAppliances: RecommendedAppliance[];
  selectedPanelApplianceId?: string;
  selectedInverterApplianceId?: string;
  selectedBatteryApplianceId?: string;
  batteryQuantity?: number;
  onBatteryQuantityChange?: (quantity: number) => void;
  onSelectPanelAppliance: (id: string) => void;
  onSelectInverterAppliance: (id: string | null) => void;
  onSelectBatteryAppliance: (id: string) => void;
  includeBattery: boolean;
  setIncludeBattery: (on: boolean) => void;
  panelsCount: number;
  /** Roof faces with included panels, rather than every drawn roof face. */
  populatedRoofArraysCount: number;
  /** Selected array capacity, used to explain an unmatched inverter. */
  arrayKwp: number | null;
  projectId: string;
  /** UK only: G98/G99 is an ENA process, not a US one. */
  showG99Hint: boolean;
  /**
   * Phases the chosen inverter connects on, from its product spec. Unknown
   * counts as single-phase, the lower G98 ceiling of the two.
   */
  inverterPhases?: 1 | 3 | null;
  /** The design allocates panels to inverter units in the Electrical tab. */
  electricalAllocationActive?: boolean;
  /**
   * Σ AC over the Electrical tab's inverter units, used instead of the single
   * Equipment pick while allocation is active. Null when a unit has no rating.
   */
  allocatedInverterAcKw?: number | null;
  /** Opens the Electrical tab, where allocated inverter units are managed. */
  onOpenElectrical?: () => void;
  /** The Electrical allocation, read for the inverter row while active. */
  electricalTopology?: SolarElectricalTopology | null;
  /**
   * The server's totalPrice(quantity) per appliance, as the totals bar uses;
   * the inverter row prefers it over unit price × count.
   */
  electricalPrices?: ReadonlyArray<{
    id: string;
    quantity?: number;
    totalPrice: { value: number };
  }>;
};

/**
 * EREC G98 issue 2 connect-and-notify ceiling: 16 A per phase, aggregated
 * across every inverter behind one connection point. Above it, G99 applies.
 */
const G98_LIMIT_KW_BY_PHASES = { 1: 3.68, 3: 11.04 } as const;

/**
 * kW on the most loaded phase of a three-phase supply: a third of each
 * three-phase unit on every phase, single-phase units packed largest first
 * onto the least loaded phase. Null when a unit's AC is unknown.
 */
export const g98WorstPhaseKw = (
  units: {
    acKw: number | null | undefined;
    phases: number | null | undefined;
  }[]
): number | null => {
  if (units.some((unit) => unit.acKw == null)) return null;
  const load = [0, 0, 0];
  for (const unit of units)
    if (unit.phases === 3)
      for (let i = 0; i < 3; i++) load[i] += unit.acKw! / 3;
  const single = units
    .filter((unit) => unit.phases !== 3)
    .map((unit) => unit.acKw!)
    .sort((a, b) => b - a);
  for (const kw of single) load[load.indexOf(Math.min(...load))] += kw;
  return Math.round(Math.max(...load) * 100) / 100;
};

/** Equipment-tab content. The inspector owns scrolling and persistent totals. */
const ProductsPanel: FC<ProductsPanelProps> = (props) => {
  const {
    recommendedPanelAppliances,
    recommendedInverterAppliances,
    excludedInverterAppliances,
    recommendedBatteryAppliances,
    selectedPanelApplianceId,
    selectedInverterApplianceId,
    selectedBatteryApplianceId,
    batteryQuantity = 1,
    onBatteryQuantityChange,
    onSelectPanelAppliance,
    onSelectInverterAppliance,
    onSelectBatteryAppliance,
    includeBattery,
    setIncludeBattery,
    panelsCount,
    arrayKwp,
    populatedRoofArraysCount,
    projectId,
    showG99Hint,
    inverterPhases,
    electricalAllocationActive = false,
    onOpenElectrical,
    allocatedInverterAcKw,
    electricalTopology = null,
    electricalPrices,
  } = props;
  const supplyPhases = inverterPhases ?? 1;
  const [expandedSection, setExpandedSection] = useState<ApplianceKind | null>(
    null
  );
  const methods = useForm<EquipmentValues>({
    values: {
      panel: selectedPanelApplianceId ?? '',
      inverter: selectedInverterApplianceId ?? '',
      battery: selectedBatteryApplianceId ?? '',
      includeBattery,
      batteryQuantity,
    },
  });
  const inverterRef = useRef<HTMLDivElement>(null);
  const equipmentRef = useRef<HTMLDivElement>(null);
  const { focusRequest, onFocusHandled } = props;
  useEffect(() => {
    const destination = focusRequest?.destination;
    if (destination !== 'inverter' && destination !== 'equipment') return;
    if (destination === 'inverter') setExpandedSection('inverter');
    const target =
      destination === 'inverter' ? inverterRef.current : equipmentRef.current;
    target?.scrollIntoView?.({ block: 'start' });
    target?.focus({ preventScroll: true });
    onFocusHandled?.();
  }, [focusRequest, onFocusHandled]);

  const noSuitableInverter =
    !electricalAllocationActive &&
    panelsCount > 0 &&
    recommendedInverterAppliances.length === 0;
  // Excluded candidates still carry authoritative quantity/pricing. Only the
  // backend's explicit null permits a manual advisory choice; absent metadata
  // from an older backend never grants permission to select.
  const manualChoices = (excludedInverterAppliances ?? []).filter(
    (
      candidate
    ): candidate is RecommendedAppliance & {
      exclusionReason?: string | null;
      blockingReason: null;
    } =>
      candidate.blockingReason === null && 'totalPrice' in candidate.appliance
  );
  const inverterChoices = [
    ...recommendedInverterAppliances,
    ...manualChoices.filter(
      ({ appliance }) =>
        !recommendedInverterAppliances.some(
          (candidate) => candidate.appliance.id === appliance.id
        )
    ),
  ];
  const selectedInverter = inverterChoices.find(
    ({ appliance }) => appliance.id === selectedInverterApplianceId
  );
  const selectedReview = excludedInverterAppliances?.find(
    ({ appliance }) => appliance.id === selectedInverterApplianceId
  );
  const selectedBattery =
    recommendedBatteryAppliances.find(
      ({ appliance }) => appliance.id === selectedBatteryApplianceId
    ) ??
    (selectedInverter?.appliance.id === selectedBatteryApplianceId
      ? selectedInverter
      : undefined);
  const combined = selectedInverter?.appliance.productSpec.pvConversion != null;
  let inverterAcKw =
    selectedInverter?.totalAcKw ??
    selectedInverter?.appliance.productSpec.acOutputKw;
  if (combined)
    inverterAcKw =
      batteryQuantity === 1
        ? selectedInverter?.appliance.productSpec.pvConversion?.acOutputKw
        : null;
  if (electricalAllocationActive && allocatedInverterAcKw !== undefined)
    inverterAcKw = allocatedInverterAcKw;
  // Only an AC-coupled battery (its own inverter) adds to the AC at the
  // connection point, rated by its charge/discharge power or AC output. A
  // DC-coupled battery exports through the PV inverter and adds nothing; an
  // unknown coupling leaves the combined output unverified.
  const batteryCoupling =
    selectedBattery?.appliance.productSpec.integratedInverter;
  const acCoupledBattery = includeBattery && batteryCoupling === true;
  const unknownBatteryCoupling = includeBattery && batteryCoupling == null;
  const batteryAcKw = acCoupledBattery
    ? (selectedBattery?.appliance.productSpec.maxChargeDischargeKw ??
      selectedBattery?.appliance.productSpec.acOutputKw)
    : null;
  let aggregateAcKw =
    (acCoupledBattery && batteryQuantity > 1) ||
    unknownBatteryCoupling ||
    inverterAcKw == null ||
    (acCoupledBattery && batteryAcKw == null)
      ? null
      : Math.round(((inverterAcKw ?? 0) + (batteryAcKw ?? 0)) * 100) / 100;
  if (combined) aggregateAcKw = inverterAcKw ?? null;
  const g98LimitKw = G98_LIMIT_KW_BY_PHASES[supplyPhases];
  // On a three-phase supply every phase must also stay within 16 A (3.68 kW):
  // a single-phase unit carries all of its output on one phase.
  const unitSpecFor = (applianceId: string | null | undefined) =>
    (
      inverterChoices.find(({ appliance }) => appliance.id === applianceId) ??
      (excludedInverterAppliances ?? []).find(
        (candidate): candidate is RecommendedAppliance =>
          candidate.appliance.id === applianceId &&
          'totalPrice' in candidate.appliance
      )
    )?.appliance.productSpec;
  const generators: {
    acKw: number | null | undefined;
    phases: number | null | undefined;
  }[] = electricalAllocationActive
    ? (electricalTopology?.inverterInstances ?? []).map((instance) => {
        const spec = unitSpecFor(
          electricalTopology?.equipmentSelections.find(
            (item) => item.id === instance.equipmentSelectionId
          )?.applianceId
        );
        return {
          acKw: spec?.acOutputKw ?? spec?.pvConversion?.acOutputKw ?? null,
          phases: spec?.phases,
        };
      })
    : [
        {
          acKw: inverterAcKw,
          phases: selectedInverter?.appliance.productSpec.phases,
        },
      ];
  if (acCoupledBattery)
    for (let unit = 0; unit < batteryQuantity; unit++)
      generators.push({
        acKw: batteryAcKw,
        phases: selectedBattery?.appliance.productSpec.phases,
      });
  const worstPhaseKw =
    supplyPhases === 3 && generators.length
      ? g98WorstPhaseKw(generators)
      : null;
  const g98PhaseLimitKw = G98_LIMIT_KW_BY_PHASES[1];
  const phaseOverG98 = worstPhaseKw != null && worstPhaseKw > g98PhaseLimitKw;
  const likelyG99 =
    showG99Hint &&
    (phaseOverG98 ||
      (inverterAcKw != null && inverterAcKw > g98LimitKw) ||
      (aggregateAcKw != null
        ? aggregateAcKw > g98LimitKw
        : !combined && arrayKwp != null && arrayKwp / 1.25 > g98LimitKw));
  let g99Line = 'G99 application likely · array above the G98 limit';
  const supplyLabel = supplyPhases === 3 ? 'three-phase' : 'single-phase';
  const g99LineFor = (kw: number) =>
    `G99 application likely · ${kw} kW AC on a ${supplyLabel} supply (G98 limit ${g98LimitKw} kW)`;
  if (aggregateAcKw != null && aggregateAcKw > g98LimitKw) {
    g99Line = g99LineFor(aggregateAcKw);
  } else if (inverterAcKw != null && inverterAcKw > g98LimitKw) {
    g99Line = g99LineFor(inverterAcKw);
  } else if (phaseOverG98) {
    g99Line = `G99 application likely · ${worstPhaseKw} kW AC on one phase (G98 limit ${g98PhaseLimitKw} kW per phase)`;
  } else if (aggregateAcKw != null) {
    g99Line = g99LineFor(aggregateAcKw);
  }

  const batterySpec = selectedBattery?.appliance.productSpec;
  const inverterSpec = selectedInverter?.appliance.productSpec;
  let mpptCount =
    inverterSpec?.inverterTopology === 'MICRO' ? null : inverterSpec?.mpptCount;
  if (combined) mpptCount = inverterSpec?.pvConversion?.mpptCount;
  const mixedManufacturerStorage =
    includeBattery &&
    batterySpec != null &&
    inverterSpec != null &&
    batterySpec.integratedInverter !== true &&
    batterySpec.manufacturer !== inverterSpec.manufacturer;
  // Storage advisories sit under the item they describe, one line each.
  const combinedAcAdvisory = includeBattery && aggregateAcKw == null && (
    <Alert colour="yellow">
      <div className="flex items-center justify-between gap-2">
        <Text>Combined AC output unverified</Text>
        <Hint text="Confirm system power limits and DNO requirements" />
      </div>
    </Alert>
  );
  const combinedAcUnderInverter = combined && !electricalAllocationActive;
  const mixedManufacturerAdvisory = mixedManufacturerStorage && (
    <Alert colour="yellow">
      <div className="flex items-center justify-between gap-2">
        <Text>Battery compatibility needs review</Text>
        <Hint
          text={`${batterySpec.manufacturer} battery with a ${inverterSpec.manufacturer} inverter`}
        />
      </div>
    </Alert>
  );
  // Allocated units grouped by product, priced at the unit price.
  const allocatedGroups = new Map<
    string,
    {
      applianceId?: string | null;
      name: string;
      count: number;
      unit?: RecommendedAppliance;
    }
  >();
  for (const instance of electricalTopology?.inverterInstances ?? []) {
    const applianceId = electricalTopology?.equipmentSelections.find(
      (item) => item.id === instance.equipmentSelectionId
    )?.applianceId;
    const key = applianceId ?? instance.equipmentSelectionId;
    const unit =
      inverterChoices.find(({ appliance }) => appliance.id === applianceId) ??
      (excludedInverterAppliances ?? []).find(
        (candidate): candidate is RecommendedAppliance =>
          candidate.appliance.id === applianceId &&
          'totalPrice' in candidate.appliance
      );
    const group = allocatedGroups.get(key) ?? {
      applianceId,
      name: unit?.appliance.productSpec.name ?? 'Unknown inverter',
      count: 0,
      unit,
    };
    group.count += instance.quantity;
    allocatedGroups.set(key, group);
  }
  const groups = [...allocatedGroups.values()];
  const allocatedPrice = groups.every((group) => group.unit)
    ? groups.reduce(
        (sum, group) =>
          sum +
          (electricalPrices?.find(
            (entry) =>
              entry.id === group.applianceId && entry.quantity === group.count
          )?.totalPrice.value ??
            (group.unit?.appliance.totalPrice.value ?? 0) * group.count),
        0
      )
    : null;
  const allocatedCurrency = groups[0]?.unit?.appliance.totalPrice.currency;
  const capacityKwh = selectedBattery
    ? installedBatteryCapacityKwh(
        selectedBattery.appliance.productSpec.usableCapacityKwh,
        batteryQuantity
      )
    : null;
  const quantityStepper = includeBattery && selectedBattery && (
    <Controller
      control={methods.control}
      name="batteryQuantity"
      render={({ field }) => (
        <IncrementableNumberInput
          aria-label="Battery quantity"
          wrapperClassName="w-32"
          sizeVariant="sm"
          min={1}
          value={field.value}
          onBlur={field.onBlur}
          onChange={(value) => {
            if (value != null && Number.isSafeInteger(value) && value >= 1) {
              field.onChange(value);
              onBatteryQuantityChange?.(value);
            }
          }}
        />
      )}
    />
  );

  let emptyInverterText =
    'No preferred inverters. Add inverters in appliances settings.';
  if (noSuitableInverter)
    emptyInverterText = selectedInverterApplianceId
      ? 'Selected inverter unavailable · check details'
      : 'Inverter not selected · panel-only pricing';

  return (
    <div
      ref={equipmentRef}
      tabIndex={-1}
      aria-label="Equipment settings"
      className="[&_button]:min-h-11 [&_button]:min-w-11"
    >
      <Form
        methods={methods}
        onSubmit={() => undefined}
        className="space-y-3"
        aria-label="Design equipment"
      >
        {!electricalAllocationActive &&
          !!selectedInverter?.warnings?.length && (
            <Alert colour="yellow">
              {selectedInverter.warnings.length === 1 ? (
                <Text>{selectedInverter.warnings[0]}</Text>
              ) : (
                <div className="flex items-center justify-between gap-2">
                  <Text>
                    {selectedInverter.warnings.length} checks to review
                  </Text>
                  <Hint text={selectedInverter.warnings.join(' · ')} />
                </div>
              )}
            </Alert>
          )}
        {likelyG99 && (
          <Alert colour="yellow">
            <div className="flex flex-wrap items-center gap-2">
              <div className="min-w-0 flex-1">
                <Text>{g99Line}</Text>
              </div>
              <AnchorButton
                as={Link}
                to={`/projects/${projectId}/property`}
                variant="secondary"
                size="sm"
              >
                Open DNO applications
              </AnchorButton>
            </div>
          </Alert>
        )}
        {noSuitableInverter && (
          <Alert colour="yellow">
            <Text>
              No automatic match
              {arrayKwp != null ? ` · ${arrayKwp.toFixed(1)} kWp array` : ''}
            </Text>
          </Alert>
        )}
        <ApplianceSection
          name="panel"
          title="Panels"
          recommendations={recommendedPanelAppliances}
          selectedApplianceId={selectedPanelApplianceId}
          panelsCount={panelsCount}
          emptyText="No preferred solar panels. Add panels in appliances settings."
          expanded={expandedSection === 'panel'}
          onExpandedChange={(open) => setExpandedSection(open ? 'panel' : null)}
          onSelect={onSelectPanelAppliance}
        />
        {electricalAllocationActive ? (
          <section
            ref={inverterRef}
            tabIndex={-1}
            aria-label="Inverter units"
            className="flex items-center justify-between gap-3 border-t border-gray-200 pt-3"
          >
            <Text>
              Inverter ·{' '}
              {groups.length
                ? groups
                    .map((group) => `${group.count} × ${group.name}`)
                    .join(', ')
                : 'none'}
              {allocatedPrice != null && allocatedCurrency && (
                <>
                  {' · '}
                  <CurrencyValue
                    currency={allocatedCurrency}
                    value={allocatedPrice}
                  />
                </>
              )}
            </Text>
            {onOpenElectrical && (
              <Button
                variant="quaternary"
                size="sm"
                aria-label="Change inverter units"
                onClick={onOpenElectrical}
              >
                Change
              </Button>
            )}
          </section>
        ) : (
          <div
            ref={inverterRef}
            tabIndex={-1}
            aria-label="Inverter choices"
            className="border-t border-gray-200 pt-3"
          >
            <ApplianceSection
              name="inverter"
              hasReviewOptions={!!excludedInverterAppliances?.length}
              title={combined ? 'PV and storage' : 'Inverter'}
              unitQuantity={combined ? batteryQuantity : undefined}
              trailing={combined ? quantityStepper : undefined}
              recommendations={inverterChoices}
              suggestedApplianceId={
                recommendedInverterAppliances[0]?.appliance.id
              }
              selectedApplianceId={selectedInverterApplianceId}
              emptyText={emptyInverterText}
              expanded={expandedSection === 'inverter'}
              onExpandedChange={(open) =>
                setExpandedSection(open ? 'inverter' : null)
              }
              onSelect={onSelectInverterAppliance}
            />
            {combinedAcUnderInverter && combinedAcAdvisory}
            {selectedReview && (
              <div className="flex items-center justify-between gap-2">
                <Text>
                  {selectedReview.blockingReason === null
                    ? 'Selected · needs review'
                    : 'Selected item cannot be used'}
                </Text>
                <Hint
                  text={
                    selectedReview.blockingReason ??
                    selectedReview.exclusionReason ??
                    'Specification data is missing'
                  }
                />
              </div>
            )}
            {selectedInverterApplianceId && (
              <Button
                variant="quaternary"
                onClick={() => onSelectInverterAppliance(null)}
              >
                Remove inverter selection
              </Button>
            )}
            {selectedInverter && (
              <div className="mt-3 space-y-3">
                <Text level="supporting">
                  {inverterSpec?.inverterTopology === 'MICRO'
                    ? `Module-level conversion: ${selectedInverter.quantity ?? 1} units · ${inverterAcKw ?? 'Unknown'} kW AC`
                    : `Independent MPPTs: ${mpptCount ?? 'Not provided'}`}
                </Text>
                {mpptCount != null && populatedRoofArraysCount > mpptCount && (
                  <Alert colour="yellow">
                    <Text>
                      Confirm the stringing design · {populatedRoofArraysCount}{' '}
                      roof arrays, {mpptCount} MPPTs
                    </Text>
                  </Alert>
                )}
              </div>
            )}
            {!!excludedInverterAppliances?.length &&
              expandedSection === 'inverter' && (
                <section
                  aria-label="Preferred inverter review"
                  className="mt-3 space-y-2"
                >
                  <Text>
                    <strong>Other preferred options</strong>
                  </Text>
                  {excludedInverterAppliances.map(
                    ({ appliance, exclusionReason, blockingReason }) => (
                      <div
                        key={appliance.id}
                        className="flex items-center justify-between gap-2"
                      >
                        <Text level="supporting">
                          <strong>{appliance.productSpec.name}</strong>
                          <br />
                          {blockingReason === null
                            ? 'Needs review'
                            : 'Cannot select'}
                        </Text>
                        <Hint
                          text={
                            blockingReason ??
                            exclusionReason ??
                            'Specification data is missing'
                          }
                        />
                      </div>
                    )
                  )}
                </section>
              )}
          </div>
        )}
        <div className="space-y-2 border-t border-gray-200 pt-3">
          <Controller
            control={methods.control}
            name="includeBattery"
            render={({ field: { name, value, onChange, onBlur, ref } }) => (
              <Field name={name}>
                <Checkbox
                  ref={ref}
                  label="Include a battery"
                  checked={value}
                  onBlur={onBlur}
                  onChange={(event) => {
                    const included = event.target.checked;
                    onChange(included);
                    setIncludeBattery(included);
                    if (!included && expandedSection === 'battery') {
                      setExpandedSection(null);
                    }
                  }}
                />
              </Field>
            )}
          />
          {includeBattery && !combined && (
            <ApplianceSection
              name="battery"
              title="Battery"
              unitQuantity={batteryQuantity}
              trailing={quantityStepper}
              recommendations={recommendedBatteryAppliances}
              selectedApplianceId={selectedBatteryApplianceId}
              emptyText="No preferred batteries. Add batteries in appliances settings."
              expanded={expandedSection === 'battery'}
              onExpandedChange={(open) =>
                setExpandedSection(open ? 'battery' : null)
              }
              onSelect={onSelectBatteryAppliance}
            />
          )}
          {!combinedAcUnderInverter && combinedAcAdvisory}
          {mixedManufacturerAdvisory}
          {includeBattery &&
            selectedBattery &&
            combined &&
            electricalAllocationActive && (
              <div className="flex items-center justify-between gap-3">
                <Text level="supporting">Units</Text>
                {quantityStepper}
              </div>
            )}
          {includeBattery && selectedBattery && (
            <div className="flex items-center justify-between gap-3">
              <Text level="supporting">Storage</Text>
              <Text>
                {capacityKwh != null ? `${capacityKwh} kWh` : 'Not provided'}
              </Text>
            </div>
          )}
        </div>
      </Form>
    </div>
  );
};

export default ProductsPanel;
